import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Box, Card } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { actionSetDraftReservation } from '../../store/Reservations/ReservationsAction';
import { actionGetVenueRequest } from '../../store/Venues/VenuesAction';
import { actionSetSearchOverlayVisible } from '../../store/Search/SearchAction';
import {
  convertUserAllergies,
  convertUserDiets,
} from '../../utils/reservationFilters';
import {
  BOOK_A_TABLE_ACCESSIBILITY_ALT_TEXT,
  BOOK_A_TABLE_TEXT,
  EVENT_TEXT,
  GET_TICKETS_ACCESSIBILITY_ALT_TEXT,
  GET_TICKETS_TEXT,
  SOLD_OUT_BADGE_TEXT,
  VENUE_IMAGE_FROM_TEXT,
  UNABLE_TO_LOAD_AVAILABLE_DATES_AND_TIMES_FOR_VENUES,
  INLINE_AVAILABILITY_ERROR_MESSAGE,
} from '../../assets/copy';
import useEventFlag from '../../utils/useEventFlag';
import { isNewVenue } from '../../utils/venue';
import {
  convertNullToEmptyString,
  generateScreenReaderText,
} from '../../utils/stringHelperMethods';
import TextIcon from '../../components/core/TextIcon';
import {
  displayCardEventDates,
  displayCardEventTimes,
  displayLocationText,
  isSoldOut,
  joinText,
} from '../../utils/eventHelpers';
import SignatureCollectionBadge from '../../components/core/Badge/SignatureCollectionBadge';
import NewRestaurantBadge from '../../components/core/Badge/NewRestaurantBadge';
import { useShortVenueDetails } from '../../utils/useShortVenueDetails';
import { cardHoverStyles } from '../../utils/constants/hoverStyles';
import Typography from '../../components/overrides/Typography';
import VenueResultAvailability from '../../components/search/VenueResultAvailability';
import { OutlinedButton } from '../../components/spoon/Button';
import { STEPS } from '../../components/spoon/TableBookingModal';
import {
  trackClickWithDescription,
  trackErrorPageView,
  trackPageImpression,
} from '../../utils/useOneTag';
import mediaImg from '../../assets/images/media.png';
import { TIME_AVAILABILITY_TYPES } from '../venueAvailability/timeAvailabilityTypes';
import { usePress } from '@react-aria/interactions';
import Badge from '../../components/core/Badge';
import LocationBadge from '../../components/core/Badge/EventLocationBadge';
import useCustomIntersectionObserver from '../../utils/useCustomIntersectionObserver';
import { checkIndexOfSelectedDate } from '../../utils/generateAvailabilityInput';
import InfoBlockMessage from '../../components/core/InfoBlock/InfoBlockMessage';
import WarningThick from '../../assets/icons/warning-thick.svg';
import { useCopy } from '../../utils/useCopy';
import ErrorPageView from '../../utils/ErrorPageView';

const useStyles = makeStyles((theme) => ({
  root: {
    boxSizing: 'border-box',
    padding: theme.spacing(3),
    flexGrow: 1,
    [theme.breakpoints.up('xl')]: {
      padding: theme.spacing(9, 5, 3),
    },
  },
  media: {
    maxWidth: '100%',
    width: 20,
    height: 20,
    marginLeft: theme.spacing(2.5),
    [theme.breakpoints.up('xl')]: {
      marginLeft: theme.spacing(2),
      width: 31,
    },
  },
  badges: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 1,
    display: 'flex',
    flexFlow: 'row wrap',
  },
  badgeContainer: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRadius: 4,
  },
  textContainer: {
    position: 'relative',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('xl')]: {
      paddingLeft: theme.spacing(4.5),
      paddingRight: theme.spacing(4.5),
    },
  },
  buttonWrapper: {
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up('xl')]: {
      paddingLeft: theme.spacing(4.5),
    },
  },
  textContainerWithAccolades: {
    paddingTop: theme.spacing(1),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: 130,
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(4),
  },
  bookingOrAvailabilityContainer: {
    paddingTop: theme.spacing(1),
  },
  cardUpperText: {
    paddingBottom: 5,
    paddingTop: theme.spacing(0.75),
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
  },
  venueLowerText: {
    color: '#6E6E6E',
  },
  eventLowerText: {
    color: '#000000',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
  },
  awards: {
    [theme.breakpoints.up('xl')]: {
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
  accoladesText: {
    textTransform: 'uppercase',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
  },
  title: {
    marginBottom: theme.spacing(1.5),
    color: theme.palette.black,
    [theme.breakpoints.up('xl')]: {
      marginBottom: theme.spacing(3),
    },
  },
  divider: {
    background: theme.palette.gray[30],
    marginTop: theme.spacing(5),
  },
  contentWrapper: {
    position: 'relative',
    zIndex: '100',
    maxWidth: '10em',
    height: '1.5em',
    margin: '5px',
    borderRadius: '14px',
    float: 'left',
    top: '-200px',
    border: '1px solid',
    [theme.breakpoints.up('xl')]: {
      position: 'relative',
      zIndex: '100',
      maxWidth: '10em',
      height: '1.5em',
      margin: '5px',
      borderRadius: '14px',
      float: 'left',
      top: '-200px',
      border: '1px solid',
    },
  },
  slideImage: {
    width: '100%',
    height: '144px',
    objectFit: 'cover',
    borderRadius: '4px',
  },
  eventSlideImage: {
    height: '182px',
    paddingBottom: theme.spacing(2),
  },
  card: ({ onCardClick }) => ({
    boxShadow: 'none',
    border: '1px solid',
    borderColor: theme.palette.gray[15],
    borderRadius: theme.spacing(1),
    width: '100%',
    height: 'auto',
    ...(onCardClick && cardHoverStyles),
    [theme.breakpoints.down('lg')]: {
      borderRadius: theme.spacing(0.5),
      width: '100%',
    },
    '&:hover': {
      cursor: 'pointer',
      boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.15)',
    },
    maxWidth: '361px',
  }),
  accoladesIcon: {
    filter: 'brightness(0) grayscale(1)',
  },

  availTime: {
    gridRow: 2,
    marginLeft: '-24px',
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up('xl')]: {
      marginLeft: 0,
      paddingLeft: theme.spacing(4.5),
    },
  },
  description: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up('xl')]: {
      paddingLeft: theme.spacing(4.5),
    },
  },
  icon: {
    marginRight: theme.spacing(0.5),
  },
  eventDateText: {
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
}));

const VenueResult = ({
  venue,
  onVenueSelect,
  index,
  bookingModalOpen,
  setBookingModalOpen,
  bookingModalVenue,
  setBookingModalVenue,
  openBookingModal,
  setStepOverride,
  showAvailabilityError = false,
  ...cardProps
}) => {
  const awards =
    venue.awards?.items.length > 0
      ? venue.awards.items.map((item) => item.award)
      : null;
  const hasAccolades = awards && awards.length > 0;
  const classes = useStyles({});
  const dispatch = useDispatch();
  const history = useHistory();
  const { getCopy } = useCopy();
  const { inLocalMarket } = useSelector((state) => state.venues);
  const { user } = useSelector((state) => state.user);
  const relatedVenues = venue?.relatedVenues?.items;
  const eventStatus = useEventFlag(venue);
  const locationBadgeText = eventStatus.venueHasEvent
    ? displayLocationText(venue, relatedVenues)
    : null;
  const availability = useSelector(
    (state) =>
      state.availability.listAvailabilityVenues[venue.sevenRoomsVenueId]
  );
  const wrapperStyle = showAvailabilityError
    ? { height: 175, paddingBottom: 16 }
    : {};
  const { refinedGuests, refinedTime } = useSelector(
    (state) => state.availability
  );
  const eventText = eventStatus.venueHasEvent
    ? convertNullToEmptyString(eventStatus?.renderVenueBadge()?.props?.text) +
      ' ' +
      EVENT_TEXT
    : '';

  useEffect(() => {
    if (showAvailabilityError) {
      trackErrorPageView(
        new ErrorPageView({
          errorTitle: getCopy('AVAILABILITY_ERROR_IN_SEARCH_PAGE_TITLE', {
            venueName: venue?.name,
            venueId: venue?.id,
          }),
          errorMessage: INLINE_AVAILABILITY_ERROR_MESSAGE,
          value: 'AVAILABILITY_ERROR',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAvailabilityError, venue?.id, venue?.name]);

  const defaultTimeslotIndex = useMemo(() => {
    if (!availability) return;
    const index = checkIndexOfSelectedDate(
      availability.date,
      refinedTime,
      availability.timeslots
    );
    return index;
  }, [availability, refinedTime]);

  const getScreenReaderDescription = () => {
    return generateScreenReaderText(
      ',',
      eventText,
      venue.name,
      getShortVenueDescription(venue),
      venue.market?.name
    );
  };
  // noinspection JSUnusedLocalSymbols
  const getCardMediaTitle = (venueName) => {
    return generateScreenReaderText(',', VENUE_IMAGE_FROM_TEXT, venueName);
  };
  const handleSelectTimeSlot = useCallback(
    async (date, time, timeslot) => {
      trackClickWithDescription(
        'search-revamp-result-venue-card-timeslot',
        'Button',
        'venue: ' + venue.name + ', date: ' + date + ', time: ' + time
      );
      const draftReservation = {
        venue,
        userId: user.id,
        c1CustRefId: user.c1CustRefId,
        defaultTipAmount: user.defaultTipAmount,
        allergies: convertUserAllergies(user.allergies),
        diets: convertUserDiets(user.diets),
        notes: user.notes || '',
        emailAddress: user.emailAddress,
        specialOccasions: [],
        phoneNumber: user.phoneNumber,
        firstName: user.firstName,
        lastName: user.lastName,
        ...timeslot,
        date,
        time: time,
        guests: parseInt(refinedGuests.split(' ')?.[0]),
      };

      if (timeslot.slotName === '') {
        dispatch(actionSetSearchOverlayVisible(false));
        await Promise.all([
          dispatch(actionSetDraftReservation(draftReservation)),
          dispatch(actionGetVenueRequest(draftReservation.venue.id)),
        ]);
        setStepOverride(STEPS.CONFIRM_RESERVATION);
      } else {
        await Promise.all([
          dispatch(actionSetDraftReservation(draftReservation)),
          dispatch(actionGetVenueRequest(draftReservation.venue.id)),
        ]);
        setStepOverride(STEPS.SELECT_DINING_OPTION);
      }
      openBookingModal(venue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [venue, user, refinedGuests]
  );

  const renderBadges = () => {
    return (
      <Box className={classes.badges}>
        {venue.isSignatureCollection && (
          <Box className={classes.badgeContainer}>
            <SignatureCollectionBadge />
          </Box>
        )}
        {isNewVenue(venue) && (
          <Box className={classes.badgeContainer}>
            <NewRestaurantBadge />
          </Box>
        )}
        {locationBadgeText && (
          <Box className={classes.badgeContainer}>
            <LocationBadge location={locationBadgeText} />
          </Box>
        )}
        {isSoldOut(venue) && (
          <Box className={classes.badgeContainer}>
            <Badge text={SOLD_OUT_BADGE_TEXT} type="soldOutChip" />
          </Box>
        )}
      </Box>
    );
  };
  const { getShortVenueDescription } = useShortVenueDetails();

  const availabilityState = useSelector((state) => state.availability);
  const isGuestDateTimeSelected = (availabilityState) => {
    return (
      availabilityState.refinedDate &&
      availabilityState.refinedTime &&
      availabilityState.refinedGuests
    );
  };
  const handleOutlineButtonPress = useCallback(
    () => {
      if (eventStatus.venueHasEvent) {
        trackClickWithDescription(
          'search-revamp-result-venue-card-get-tickets-cta',
          'Button',
          venue.name
        );
        onVenueSelect(venue.id);
      } else {
        trackClickWithDescription(
          'search-revamp-result-venue-card-book-a-table-cta',
          'Button',
          venue.name
        );
        openBookingModal(venue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventStatus, onVenueSelect, venue]
  );
  const { pressProps: outlineButtonPress } = usePress({
    onPress: handleOutlineButtonPress,
  });

  const handleCardPress = useCallback(() => {
    trackClickWithDescription(
      'search-revamp-result-venue-card',
      'Card',
      venue?.name
    );
    onVenueSelect(venue?.id);
  }, [onVenueSelect, venue?.id, venue?.name]);
  const { pressProps: cardPressProps } = usePress({ onPress: handleCardPress });
  const renderAvailabilityOrBookingCTA = useCallback(
    (isGuestDateTimeSelected) => {
      if (isGuestDateTimeSelected) {
        return inLocalMarket ? (
          <VenueResultAvailability
            availability={availability}
            className={classes.availTime}
            type={TIME_AVAILABILITY_TYPES.HORIZONTAL_SEARCH_REFRESH}
            handleClick={handleSelectTimeSlot}
            venueId={venue.id}
            venueStartOfDay={venue.startOfDay}
            venueParent={venue}
            defaultTimeslotIndex={defaultTimeslotIndex}
          />
        ) : null;
      } else {
        return (
          <div className={classes.buttonWrapper} {...cardPressProps}>
            <OutlinedButton
              {...outlineButtonPress}
              size={'smallAltFocus'}
              focusRipple={false}
              aria-label={
                (eventStatus.venueHasEvent
                  ? GET_TICKETS_ACCESSIBILITY_ALT_TEXT
                  : BOOK_A_TABLE_ACCESSIBILITY_ALT_TEXT) + venue.name
              }>
              {eventStatus.venueHasEvent ? GET_TICKETS_TEXT : BOOK_A_TABLE_TEXT}
            </OutlinedButton>
          </div>
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      availability,
      classes.availTime,
      classes.buttonWrapper,
      handleSelectTimeSlot,
      inLocalMarket,
      venue,
      outlineButtonPress,
      eventStatus.venueHasEvent,
      cardPressProps,
    ]
  );

  const { stepUp, draftReservation } = useSelector(
    (state) => state.reservations
  );

  useEffect(() => {
    if (stepUp?.shouldRedirect) {
      if (stepUp?.isModal) {
        if (index === 0) {
          // refrains from opening modals for every VenueResult
          setStepOverride(STEPS.CONFIRM_RESERVATION);
          openBookingModal(draftReservation?.venue);
        }
      } else {
        history.push('/reserve');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, stepUp, draftReservation, index]);

  const handleCardKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        trackClickWithDescription(
          'search-revamp-result-venue-card',
          'Card',
          venue.name
        );
        onVenueSelect(venue.id);
      }
    },
    [venue, onVenueSelect]
  );

  const trackedVenues = useRef(new Set());

  const elementRef = useCustomIntersectionObserver(
    () => {
      if (!trackedVenues.current.has(venue.name)) {
        // Track the impression only if it hasn't been tracked yet
        trackPageImpression(
          'search',
          'search-revamp-result-venue-card',
          venue.name
        );
        trackedVenues.current.add(venue.name); // Mark the venue as tracked
      }
    },
    { threshold: 0.8, triggerOnce: true }
  );

  return (
    <>
      <Card
        className={classes.card}
        role={'link'}
        aria-label={`Go to ${venue.name} venue details page.`}
        tabIndex={0}
        onKeyDown={handleCardKeyDown}
        {...cardProps}
        ref={elementRef}>
        <div
          {...cardPressProps}
          style={{
            position: 'relative',
          }}>
          {renderBadges()}
          {venue && (
            <img
              src={venue.images[0] || mediaImg}
              className={clsx(classes.slideImage, {
                [classes.eventSlideImage]: eventStatus.venueHasEvent,
              })}
              alt={`${venue.name}`}
              aria-label={getCardMediaTitle(venue?.name)}
              onError={(e) => (e.target.src = mediaImg)} // Fallback image
              loading="lazy" // Lazy load the image
            />
          )}
        </div>
        <Box
          className={classes.wrapper}
          aria-label={getScreenReaderDescription()}
          style={wrapperStyle}>
          <Box {...cardPressProps}>
            <div className={classes.textContainer}>
              {eventStatus.venueHasEvent && (
                <Box component="div" minHeight={18}>
                  <Typography
                    className={classes.eventDateText}
                    variant="small4Semibold">
                    {joinText(
                      [
                        displayCardEventDates(venue.eventDates),
                        displayCardEventTimes(venue.eventTimes),
                      ],
                      ' | '
                    )}
                  </Typography>
                </Box>
              )}
              {hasAccolades && !eventStatus.venueHasEvent && (
                <Box mt={1} className={classes.awards}>
                  <TextIcon
                    key={awards[0].id}
                    src={awards[0].icon}
                    variant={'small4Semibold'}
                    text={`${joinText(
                      [awards[0].secondaryText, awards[0].primaryText],
                      ' - '
                    )}`}
                    iconStyleOverride={classes.accoladesIcon}
                    textStyleOverride={classes.accoladesText}
                  />
                </Box>
              )}
              <Typography
                variant={'medium2Light'}
                className={classes.cardUpperText}
                component="h2">
                {venue.name}
              </Typography>
              <Typography
                variant={
                  eventStatus.venueHasEvent ? 'tiny2Normal' : 'small2Normal'
                }
                className={
                  eventStatus.venueHasEvent
                    ? classes.eventLowerText
                    : classes.venueLowerText
                }
                component="h2">
                {getShortVenueDescription(venue)}
              </Typography>
            </div>
          </Box>
          <Box className={classes.bookingOrAvailabilityContainer}>
            {showAvailabilityError ? (
              <Box className={classes.textContainer}>
                <InfoBlockMessage
                  icon={WarningThick}
                  text={UNABLE_TO_LOAD_AVAILABLE_DATES_AND_TIMES_FOR_VENUES}
                  screenReaderText={
                    UNABLE_TO_LOAD_AVAILABLE_DATES_AND_TIMES_FOR_VENUES
                  }
                  role="alert"
                  altIconText="Warning icon"
                />
              </Box>
            ) : (
              renderAvailabilityOrBookingCTA(
                isGuestDateTimeSelected(availabilityState)
              )
            )}
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default VenueResult;
